import { ImagesSkusChevronDownSvg } from '@/assets/images_skus.assets';
import DefaultButton from '@/components/common/default/DefaultButton';
import { PrimaryButton } from '@/components/common/PrimaryButton';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SkusEnum } from '@/types/translations/SkusEnum';
import { Transition } from '@headlessui/react';
import Image from '@/components/common/SpiritoryImage';
import { Fragment, useEffect, useState } from 'react';
import { useRange, useHits } from 'react-instantsearch';
import useEvents from '@/hooks/useEvents';
import type { SkuDTO } from '@/types/api/sku';

export default function SkusMobileSearchShowPricesFilter() {
  const { translate } = useTypedTranslation<SkusEnum>('skus');

  const [showPrices, setShowPrices] = useState(false);

  const { start, range, canRefine, refine } = useRange({
    attribute: 'lowestAsk',
  });
  const { min = 0, max = 100 } = range;
  const [value, setValue] = useState({ start: min, end: max });
  const [displayValue, setDisplayValue] = useState({
    start: min / 100,
    end: max / 100,
  });

  const minVal = range.min !== undefined ? range.min : 0;
  const maxVal = range.max !== undefined ? range.max : 100;

  const from =
    start[0] !== undefined ? Math.min(minVal, Number.isFinite(start[0]) ? start[0] : minVal) : 0;
  const to =
    start[1] !== undefined ? Math.min(maxVal, Number.isFinite(start[1]) ? start[1] : maxVal) : 100;

  const { items } = useHits<SkuDTO>();
  const { fireSearchFilterFilledEvent } = useEvents('', items);

  useEffect(() => {
    setValue({ start: from, end: to });
    setDisplayValue({ start: from / 100, end: to / 100 });
  }, [from, to]);

  const handleStartChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStart = parseFloat(event.target.value) * 100;
    setValue({ ...value, start: newStart });
    setDisplayValue({ ...displayValue, start: parseFloat(event.target.value) });
  };

  const handleEndChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnd = parseFloat(event.target.value) * 100;
    setValue({ ...value, end: newEnd });
    setDisplayValue({ ...displayValue, end: parseFloat(event.target.value) });
  };

  const handleRefine = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    refine([value.start, value.end]);
    if (value.start !== min || value.end !== max) {
      fireSearchFilterFilledEvent();
    }
  };

  return (
    <li>
      {' '}
      <DefaultButton
        className="tw-flex tw-w-full tw-items-center tw-justify-between tw-font-plusJakartaSans !tw-text-base tw-font-semibold tw-text-textBlack"
        onClick={() => setShowPrices(prev => !prev)}
      >
        <span>{translate(SkusEnum['price'])}</span>
        <Image src={ImagesSkusChevronDownSvg} width={5.59} height={12} alt="Chevron Down" />
      </DefaultButton>
      <Transition
        as={Fragment}
        show={showPrices}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <form
          className="tw-mt-4 tw-flex tw-flex-col tw-gap-3 tw-border tw-border-l-0 tw-border-r-0 tw-border-t-0 !tw-border-solid tw-border-custom-gray tw-pb-4"
          onSubmit={handleRefine}
        >
          <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-4 child:tw-basis-2/4">
            <input
              type="number"
              name="min"
              id="min"
              placeholder="Min"
              className="tw-w-2/4 tw-rounded-md tw-border !tw-border-solid tw-border-custom-gray tw-bg-white tw-px-2 tw-py-2 tw-text-base tw-font-medium tw-text-textBlack"
              min={min / 100}
              max={max / 100}
              value={displayValue.start}
              onChange={handleStartChange}
              disabled={!canRefine}
            />
            <input
              type="number"
              name="max"
              id="max"
              placeholder="Max"
              className="tw-w-2/4 tw-rounded-md tw-border !tw-border-solid tw-border-custom-gray tw-bg-white tw-px-2 tw-py-2 tw-text-base tw-font-medium tw-text-textBlack"
              min={min / 100}
              max={max / 100}
              value={displayValue.end}
              onChange={handleEndChange}
              disabled={!canRefine}
            />
          </div>
          <PrimaryButton type="submit" disabled={!canRefine}>
            {translate(SkusEnum['apply'])}
          </PrimaryButton>
        </form>
      </Transition>
    </li>
  );
}
